<template>
  <div
    class="bui-nav-item-overflow"
    :class="{ narrow, open: isOpen }"
    @focusout="onFocusOut"
  >
    <div
      v-show="isOpen"
      class="overflow-panel"
      role="group"
      :aria-label="label"
    >
      <slot />
    </div>
    <button
      type="button"
      class="trigger"
      :data-testid="triggerTestId"
      :aria-expanded="isOpen"
      :title="narrow ? label : undefined"
      @click="toggle"
    >
      <NavIco>
        <PhCaretUpDown />
      </NavIco>
      <span v-if="!narrow" class="label">{{ label }}</span>
    </button>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import NavIco from '../../atoms/NavIco/NavIco.vue';
import { PhCaretUpDown } from '@phosphor-icons/vue';

const props = withDefaults(
  defineProps<{
    label?: string;
    narrow?: boolean;
    /** Optional controlled open state */
    open?: boolean | null;
  }>(),
  { label: 'More', narrow: false, open: null },
);

const emit = defineEmits<{ 'update:open': [value: boolean] }>();

const internalOpen = ref(false);
const isOpen = computed(() =>
  props.open !== null ? !!props.open : internalOpen.value,
);

const triggerTestId = computed(() => {
  const slug = (props.label ?? 'More')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return slug ? `nav-${slug}` : undefined;
});

function setOpen(v: boolean) {
  internalOpen.value = v;
  emit('update:open', v);
}
function toggle() { setOpen(!isOpen.value); }

function onFocusOut(e: FocusEvent) {
  const next = e.relatedTarget as Node | null;
  const root = e.currentTarget as HTMLElement | null;
  if (!root || !next || !root.contains(next)) setOpen(false);
}
</script>

<style lang="scss" scoped>
.bui-nav-item-overflow {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 100%;
  min-width: 0;
  position: relative;
  z-index: 1;

  &.open {
    z-index: 40;
  }

  /* Opens upward, overlays content above; does not grow layout or push System Status */
  .overflow-panel {
    position: absolute;
    left: 0;
    right: 0;
    bottom: calc(100% + 4px);
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    background: var(--nav-fill-2, #4b1886);
    border-radius: var(--nav-radius-overflow, 24px);
    padding: var(--nav-overflow-panel-pad-block, 8px)
      var(--nav-overflow-panel-pad-inline, 8px);
    display: flex;
    flex-direction: column;
    gap: 6px;
    z-index: 41;
    /* Never show a scrollbar on this panel; grow to fit content */
    overflow: visible;
    box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.24);

    /*
     * Concentric corners: inner row radius = pill radius − inset into the pill
     * (same idea as status chip vs row, applied to rows vs overflow container).
     */
    --nav-overflow-item-r: max(
      6px,
      calc(
        var(--nav-radius-overflow, 24px) - var(--nav-overflow-panel-pad-block, 8px)
      )
    );
  }

  /* Rows, hovers, and dropdown chrome share the stack radius */
  .overflow-panel :deep(.bui-nav-item) {
    border-radius: var(--nav-overflow-item-r);
  }

  .overflow-panel :deep(.bui-nav-item-dd .trigger) {
    border-radius: var(--nav-overflow-item-r);
  }

  .overflow-panel :deep(.bui-nav-item-dd .panel),
  .overflow-panel :deep(.bui-nav-item-dd .popover) {
    border-radius: var(--nav-overflow-item-r);
  }

  /* Win over NavItem.vue scoped status rule inside this stack */
  .overflow-panel :deep(.bui-nav-item.active .bui-nav-status-ico) {
    border-radius: max(
      0px,
      calc(
        var(--nav-overflow-item-r) - var(--nav-status-inner-gap, 4px)
      )
    ) !important;
  }

  /*
   * Collapsed rail: full-width rows with a small horizontal gutter (not 56px
   * content in a 40px slot — avoids overflow — and leaves room vs pill edge).
   */
  &.narrow .overflow-panel {
    padding-left: var(--nav-overflow-panel-pad-inline-narrow, 4px);
    padding-right: var(--nav-overflow-panel-pad-inline-narrow, 4px);

    :deep(li) {
      width: 100%;
      min-width: 0;
    }

    :deep(.bui-nav-item.narrow) {
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }

    :deep(.bui-nav-item-dd.narrow) {
      max-width: 100%;
    }

    :deep(.bui-nav-item-extra) {
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }
  }

  .trigger {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 4px 8px;
    background: transparent;
    border: 0;
    border-radius: var(--nav-radius-box, 12px);
    color: var(--nav-label-primary, #f6f7ff);
    cursor: pointer;
    font-family: inherit;
    font-size: var(--type-body-lg-size, 20px);
    line-height: var(--type-body-lg-line-height, 23px);
    width: 100%;

    &:hover { background: var(--nav-btn-hover, #8e3fbc); }
    &:focus-visible {
      outline: 2px solid var(--nav-label-primary, #f6f7ff);
      outline-offset: 2px;
    }

    .label {
      flex: 1 0 0;
      text-align: left;
    }
  }

  &.narrow {
    width: 56px;
    align-items: center;

    .trigger {
      width: 56px;
      justify-content: center;
      padding: 4px 0;
    }
  }
}
</style>
